/* global SideDrawer */

/**
 * Product Preview Panel
 *
 * A left-sliding panel that displays product content from collection pages
 * without full page navigation. Uses the prefetch cache when available.
 */
class ProductPreviewPanel extends SideDrawer {
  constructor() {
    super();
    this.contentContainer = null;
    this.loadingContainer = null;
    this.currentUrl = null;
  }

  connectedCallback() {
    this.contentContainer = this.querySelector('[data-preview-content]');
    this.loadingContainer = this.querySelector('[data-preview-loading]');
    this.titleElement = this.querySelector('[data-preview-title]');

    this.bindEvents();
  }

  bindEvents() {
    // Listen for preview button clicks (delegated)
    document.addEventListener('click', this.handlePreviewClick.bind(this));

    // Listen for cart:add to close panel and open cart drawer
    this.addEventListener('on:cart:add', this.handleCartAdd.bind(this));

    // Also listen via document in case event doesn't bubble correctly
    document.addEventListener('on:cart:add', (evt) => {
      if (this.hasAttribute('open') && this.contains(evt.target)) {
        this.handleCartAdd(evt);
      }
    });
  }

  /**
   * Handle click on preview trigger buttons
   * @param {Event} evt - Click event
   */
  handlePreviewClick(evt) {
    const trigger = evt.target.closest('[data-product-preview-trigger]');
    if (!trigger) return;

    evt.preventDefault();
    evt.stopPropagation();

    const url = trigger.dataset.productUrl;
    if (url) {
      this.loadProduct(url, trigger);
    }
  }

  /**
   * Load and display product content
   * @param {string} url - Product URL to load
   * @param {Element} opener - Element that triggered the open
   */
  loadProduct(url, opener) {
    this.currentUrl = url;

    // Show loading state
    this.showLoading();

    // Open the panel
    this.open(opener, this.querySelector('.preview-panel__close-btn'));

    // Check cache first
    const cachedContent = this.getCachedContent(url);

    if (cachedContent) {
      // Use cached content
      this.renderContent(cachedContent, url);
    } else {
      // Fetch content
      this.fetchProduct(url);
    }
  }

  /**
   * Check prefetch cache for product content
   * @param {string} url - Product URL
   * @returns {string|null} - Cached HTML or null
   */
  getCachedContent(url) {
    if (!window.prefetchCache) return null;

    // Get path without query params (matching prefetch-accelerator logic)
    const path = this.getPathWithoutQuery(url);
    const primaryPath = this.getPrimaryUrl(url);
    const cacheTimeout = (window.theme?.settings?.prefetchCachePeriod || 10) * 60000;

    // Check main path
    if (window.prefetchCache[path]) {
      const cached = window.prefetchCache[path];
      if (Date.now() - cached.timestamp < cacheTimeout) {
        return cached.html;
      }
    }

    // Check primary path (for collection-prefixed URLs)
    if (primaryPath) {
      const primaryPathClean = this.getPathWithoutQuery(primaryPath);
      if (window.prefetchCache[primaryPathClean]) {
        const cached = window.prefetchCache[primaryPathClean];
        if (Date.now() - cached.timestamp < cacheTimeout) {
          return cached.html;
        }
      }
    }

    return null;
  }

  /**
   * Get path without query params (except page)
   * @param {string} url - Full URL
   * @returns {string} - Path
   */
  getPathWithoutQuery(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      let path = parsedUrl.pathname;
      const pageParam = parsedUrl.searchParams.get('page');
      if (pageParam) {
        path += `?page=${pageParam}`;
      }
      return path;
    } catch (e) {
      return url;
    }
  }

  /**
   * Get primary URL for collection-prefixed product URLs
   * @param {string} url - URL to process
   * @returns {string|null} - Primary URL or null
   */
  getPrimaryUrl(url) {
    try {
      const urlStr = url.toString();
      if (urlStr.includes('/collections/') && urlStr.includes('/products/')) {
        return urlStr.replace(/\/collections\/[^/]+\//, '/');
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Fetch product page content
   * @param {string} url - Product URL
   */
  fetchProduct(url) {
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(html => {
        // Only render if this is still the current request
        if (this.currentUrl === url) {
          this.renderContent(html, url);
        }
      })
      .catch(error => {
        console.error('Error loading product preview:', error);
        this.showError(url);
      });
  }

  /**
   * Render product content into the panel
   * @param {string} html - Full page HTML
   * @param {string} url - Product URL
   */
  renderContent(html, url) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract product content - try specific selectors first, then fallback
    // Priority: .section-main-product content > product-form.js-product > .product-info
    let productContent = doc.querySelector('.section-main-product product-form.js-product');

    if (!productContent) {
      productContent = doc.querySelector('product-form.js-product');
    }

    if (!productContent) {
      productContent = doc.querySelector('.product-info');
    }

    if (!productContent) {
      // Fallback to #content > div but we'll filter it
      productContent = doc.querySelector('#content > div');
    }

    if (!productContent) {
      this.showError(url);
      return;
    }

    // Re-parse HTML instead of cloning to ensure custom elements properly upgrade
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = productContent.outerHTML;
    const content = tempContainer.firstElementChild;

    // Remove any modal dialogs that might be in the content
    content.querySelectorAll('modal-dialog, .modal, [role="dialog"]').forEach(el => el.remove());

    // Remove mobile duplicate elements (title/price shown above gallery on mobile)
    // These cause duplicate content in narrow containers where CSS media queries don't apply
    content.querySelectorAll('.mobile-title-duplicate, .mobile-price-duplicate').forEach(el => el.remove());

    // Hide elements meant only for quickbuy (not full preview)
    content.querySelectorAll('.only-in-quickbuy').forEach(el => el.remove());

    // Remove sticky behaviors that don't work in panel context
    content.querySelectorAll('.sticky-content-container').forEach(el => el.classList.remove('sticky-content-container'));
    content.querySelectorAll('[data-sticky]').forEach(el => el.removeAttribute('data-sticky'));

    // Remove breadcrumbs, zoom/gallery elements, and overlays
    content.querySelectorAll('.breadcrumbs, .page-header, .gallery-viewer, .photoswipe-gallery, [data-pswp-uid]').forEach(el => el.remove());

    // Unwrap images from zoom links (a.show-gallery)
    content.querySelectorAll('a.show-gallery').forEach(link => {
      const parent = link.parentNode;
      while (link.firstChild) {
        parent.insertBefore(link.firstChild, link);
      }
      link.remove();
    });

    // Force mobile layout: show mobile-only elements, hide desktop-only elements
    content.querySelectorAll('.mobile-only').forEach(el => {
      el.classList.remove('mobile-only');
      el.style.display = '';
    });
    content.querySelectorAll('.desktop-only').forEach(el => el.remove());

    // Remove desktop collage layout, keep mobile carousel
    content.querySelectorAll('.product-media-collage').forEach(el => el.remove());

    // Force carousel layout for gallery
    ['media-gallery--layout-columns-1', 'media-gallery--layout-columns-2',
     'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2',
     'media-gallery--layout-carousel-beside'].forEach(cls => {
      content.querySelectorAll(`.${cls}`).forEach(el => {
        el.classList.remove(cls);
        el.classList.add('media-gallery--layout-carousel-under');
      });
    });

    // Add mobile-gallery--dots class to enable dots styling
    content.querySelectorAll('.media-gallery').forEach(gallery => {
      gallery.classList.add('mobile-gallery--dots');
      // Remove any mobile-only class from slider-dots so they're visible
      gallery.querySelectorAll('.slider-dots.mobile-only').forEach(dots => {
        dots.classList.remove('mobile-only');
      });
    });

    // Transform content for preview panel
    this.transformContent(content);

    // Update title from product
    const productTitle = doc.querySelector('.product-title, .product-single__title, h1');
    if (productTitle && this.titleElement) {
      this.titleElement.textContent = productTitle.textContent.trim();
    }

    // Clear and inject content
    this.contentContainer.innerHTML = '';

    // Wrap in container for styling
    const wrapper = document.createElement('div');
    wrapper.className = 'preview-panel__product-content';
    wrapper.appendChild(content);

    this.contentContainer.appendChild(wrapper);

    // Hide loading, show content
    this.hideLoading();

    // Scroll content to top
    this.contentContainer.scrollTop = 0;

    // Initialize components after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeComponents();
      // Scroll to top again after components init
      this.contentContainer.scrollTop = 0;
    }, 50);
  }

  /**
   * Transform content for panel display
   * @param {Element} content - Content element to transform
   */
  transformContent(content) {
    // Suffix all IDs to prevent conflicts
    if (typeof theme !== 'undefined' && theme.suffixIds) {
      theme.suffixIds(content, '-preview');
    } else {
      // Fallback: manually suffix IDs
      content.querySelectorAll('[id]').forEach(el => {
        el.id = el.id + '-preview';
      });

      // Update for/id references
      content.querySelectorAll('[for]').forEach(el => {
        el.setAttribute('for', el.getAttribute('for') + '-preview');
      });

      // Update aria-labelledby references
      content.querySelectorAll('[aria-labelledby]').forEach(el => {
        const ids = el.getAttribute('aria-labelledby').split(' ');
        el.setAttribute('aria-labelledby', ids.map(id => id + '-preview').join(' '));
      });

      // Update aria-describedby references
      content.querySelectorAll('[aria-describedby]').forEach(el => {
        const ids = el.getAttribute('aria-describedby').split(' ');
        el.setAttribute('aria-describedby', ids.map(id => id + '-preview').join(' '));
      });
    }

    // Restore product-data ID for Klaviyo tracking (remove suffix)
    const productData = content.querySelector('#product-data-preview');
    if (productData) {
      // Remove any existing product-data from page to avoid duplicates
      const existingProductData = document.getElementById('product-data');
      if (existingProductData && !this.contains(existingProductData)) {
        existingProductData.remove();
      }
      productData.id = 'product-data';
    }

    // Prevent links from updating page URL/history
    content.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      // Keep links functional but prevent them from triggering prefetch navigation
      if (!href.startsWith('#') && !href.startsWith('javascript:')) {
        link.setAttribute('target', '_top');
      }
    });

    // Disable any URL update behaviors on variant change
    content.querySelectorAll('variant-picker, [data-variant-picker]').forEach(picker => {
      picker.setAttribute('data-update-url', 'false');
    });

    // Remove manual loading attribute so images load
    content.querySelectorAll('[loading="manual"]').forEach(img => {
      img.setAttribute('loading', 'lazy');
    });
  }

  /**
   * Initialize interactive components after content injection
   */
  initializeComponents() {
    // Force load ALL images in the panel
    this.loadAllImages();

    // Then initialize other components after images are set up
    setTimeout(() => {
      this.initCarousels();
      this.initOtherComponents();
    }, 100);
  }

  /**
   * Force load all images in the panel
   */
  loadAllImages() {
    // Use theme's manuallyLoadImages if available
    if (typeof theme !== 'undefined' && theme.manuallyLoadImages) {
      theme.manuallyLoadImages(this.contentContainer);
    }

    // Extract real images from noscript tags (theme stores actual src there)
    this.contentContainer.querySelectorAll('noscript').forEach(noscript => {
      const content = noscript.textContent || noscript.innerHTML;
      if (content.includes('<img')) {
        // Parse the noscript content to get the real image
        const temp = document.createElement('div');
        temp.innerHTML = content;
        const realImg = temp.querySelector('img');
        if (realImg && realImg.src) {
          // Find the corresponding placeholder image (previous sibling)
          const placeholder = noscript.previousElementSibling;
          if (placeholder && placeholder.tagName === 'IMG') {
            placeholder.src = realImg.src;
            if (realImg.srcset) {
              placeholder.srcset = realImg.srcset;
            }
          }
        }
      }
    });

    // Use theme's manuallyLoadImages if available
    if (typeof theme !== 'undefined' && theme.manuallyLoadImages) {
      theme.manuallyLoadImages(this.contentContainer);
    }

    // Handle images with data-manual-src (theme's lazy loading)
    this.contentContainer.querySelectorAll('img[data-manual-src]').forEach(img => {
      img.src = img.dataset.manualSrc;
      img.removeAttribute('data-manual-src');
      if (img.dataset.manualSrcset) {
        img.srcset = img.dataset.manualSrcset;
        img.removeAttribute('data-manual-srcset');
      }
      img.removeAttribute('loading');
    });

    // Handle images with data-src (common lazy loading pattern)
    this.contentContainer.querySelectorAll('img[data-src]').forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });

    // Force eager loading on all images
    this.contentContainer.querySelectorAll('img[loading="lazy"], img[loading="manual"]').forEach(img => {
      img.loading = 'eager';
    });

    // Ensure all images without src but with data attributes get their src
    this.contentContainer.querySelectorAll('img').forEach(img => {
      if (!img.src || img.src === '' || img.src === 'about:blank') {
        if (img.dataset.manualSrc) {
          img.src = img.dataset.manualSrc;
        } else if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      }
    });

    // Final pass: ensure ALL images in slider have src
    this.contentContainer.querySelectorAll('.slider__item img').forEach((img) => {
      if (!img.src || img.src === '' || img.src === 'about:blank' || img.src.includes('data:image/svg')) {
        const src = img.dataset.manualSrc || img.dataset.src || img.getAttribute('data-srcset')?.split(' ')[0];
        if (src) {
          img.src = src;
        }
      }
    });
  }

  /**
   * Initialize carousel sliders
   */
  initCarousels() {
    // Force lazy images to load eagerly
    this.contentContainer.querySelectorAll('.slider__item img[loading="lazy"]')
      .forEach(img => { img.loading = 'eager'; });

    const initSliders = () => {
      // Initialize each carousel-slider by calling its init() method
      this.contentContainer.querySelectorAll('carousel-slider').forEach(slider => {
        slider.removeAttribute('loaded');

        const slides = slider.querySelectorAll('.slider__item:not([hidden])');
        if (slides.length < 2) return;

        if (typeof slider.init === 'function') {
          try {
            slider.init();
          } catch (e) {
            // Silently handle init errors
          }
        }
      });

      // Init media-gallery
      this.contentContainer.querySelectorAll('media-gallery').forEach(gallery => {
        if (typeof gallery.init === 'function') {
          gallery.init();
        }
      });

      // Init slide-show components (used by review carousels)
      this.contentContainer.querySelectorAll('slide-show').forEach(slideshow => {
        slideshow.removeAttribute('loaded');
        if (typeof slideshow.init === 'function') {
          try {
            slideshow.init();
          } catch (e) {
            // Silently handle init errors
          }
        }
      });

      // Init any Okendo widgets
      if (typeof window.okeWidgetApi !== 'undefined' && window.okeWidgetApi.initAllWidgets) {
        window.okeWidgetApi.initAllWidgets();
      }

      // Init review slider (custom review carousel)
      if (typeof window.initReviewSlider === 'function') {
        window.initReviewSlider();
      }
    };

    // Wait for element to be upgraded, then init
    const waitAndInit = (attempts = 0) => {
      const carousel = this.contentContainer.querySelector('carousel-slider');
      if (carousel && typeof carousel.init === 'function') {
        initSliders();
      } else if (attempts < 10) {
        setTimeout(() => waitAndInit(attempts + 1), 100);
      }
    };

    setTimeout(waitAndInit, 50);
  }

  /**
   * Initialize gallery dots navigation for preview panel
   */
  initGalleryDots() {
    const mediaGallery = this.contentContainer.querySelector('.media-gallery');
    if (!mediaGallery) return;

    const slider = mediaGallery.querySelector('.main-image .slider');
    const sliderGrid = mediaGallery.querySelector('.main-image .slider__grid');
    const sliderItems = mediaGallery.querySelectorAll('.main-image .slider__item');

    if (!slider || !sliderItems.length || sliderItems.length < 2) return;

    // Check for existing dots, create if needed
    let dotsContainer = mediaGallery.querySelector('.slider-dots');
    let dots;

    if (!dotsContainer || !dotsContainer.querySelectorAll('.slider-dots__dot').length) {
      // Create dots container if it doesn't exist
      dotsContainer = document.createElement('div');
      dotsContainer.className = 'slider-dots';

      for (let i = 0; i < sliderItems.length; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider-dots__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dotsContainer.appendChild(dot);
      }

      // Insert dots after the carousel-slider element
      const carouselSlider = mediaGallery.querySelector('.main-image carousel-slider');
      if (carouselSlider) {
        carouselSlider.after(dotsContainer);
      } else {
        mediaGallery.querySelector('.main-image').appendChild(dotsContainer);
      }
    }

    dots = dotsContainer.querySelectorAll('.slider-dots__dot');

    // Calculate item width for scrolling - use slider__grid scrollWidth
    const getItemWidth = () => {
      if (sliderGrid) {
        return sliderGrid.scrollWidth / sliderItems.length;
      }
      return sliderItems[0]?.offsetWidth || 0;
    };

    // Update active dot based on scroll position
    const updateDots = () => {
      const itemWidth = getItemWidth();
      if (!itemWidth) return;

      const scrollLeft = slider.scrollLeft;
      const activeIndex = Math.round(scrollLeft / itemWidth);

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    };

    // Handle dot clicks
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const itemWidth = getItemWidth();
        slider.scrollTo({
          left: index * itemWidth,
          behavior: 'smooth'
        });
      });
    });

    // Listen for scroll to update dots
    let scrollTimeout;
    slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateDots, 50);
    });

    // Initial dot state
    setTimeout(updateDots, 100);
  }

  /**
   * Initialize other interactive components
   */
  initOtherComponents() {
    // Re-initialize variant pickers
    this.contentContainer.querySelectorAll('variant-picker').forEach(picker => {
      if (picker.init && typeof picker.init === 'function') {
        picker.init();
      }
    });

    // Re-initialize quantity selectors
    this.contentContainer.querySelectorAll('quantity-selector').forEach(qty => {
      if (qty.connectedCallback && typeof qty.connectedCallback === 'function') {
        qty.connectedCallback();
      }
    });

    // Re-initialize product forms
    this.contentContainer.querySelectorAll('product-form').forEach(form => {
      if (typeof form.init === 'function') {
        form.init();
      }
    });

    // Initialize product label pills if function exists
    if (typeof window.initializeProductLabelPills === 'function') {
      window.initializeProductLabelPills();
    }

    // Initialize mobile gallery dots if function exists
    if (typeof window.initMobileGalleryDots === 'function') {
      setTimeout(() => window.initMobileGalleryDots(), 100);
    }

    // Initialize Okendo widgets if present
    if (typeof window.okeWidgetApi !== 'undefined' && window.okeWidgetApi.initAllWidgets) {
      window.okeWidgetApi.initAllWidgets();
    }

    // Reattach Klaviyo add-to-cart tracking listeners
    if (typeof window.attachKlaviyoAddToCartListeners === 'function') {
      window.attachKlaviyoAddToCartListeners();
    }

    // Prefetch product links found in the loaded content
    this.prefetchProductLinks();

    // Dispatch event for other scripts to hook into
    this.dispatchEvent(new CustomEvent('preview-panel:content-loaded', {
      bubbles: true,
      detail: { url: this.currentUrl }
    }));
  }

  /**
   * Register product links found within the panel content with the prefetch
   * accelerator's acquisition layer. All cache writes go through the
   * accelerator's single bounded writer — never write to window.prefetchCache
   * directly from here (it would bypass the concurrency cap, connection
   * gates, response validation and eviction; see handover
   * sprint-2026-08-prefetch-v2-intent-and-tracking/002 finding 3).
   */
  prefetchProductLinks() {
    const accel = window.PrefetchAccel && window.PrefetchAccel.instance;
    if (accel && typeof accel.registerLinksWithin === 'function') {
      accel.registerLinksWithin(this.contentContainer);
    }
  }

  /**
   * Show loading state
   */
  showLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = '';
    }
    if (this.contentContainer) {
      this.contentContainer.style.display = 'none';
    }
    if (this.titleElement) {
      this.titleElement.textContent = '';
    }
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'none';
    }
    if (this.contentContainer) {
      this.contentContainer.style.display = '';
    }
  }

  /**
   * Show error state
   * @param {string} url - Product URL for fallback link
   */
  showError(url) {
    this.hideLoading();
    this.contentContainer.innerHTML = `
      <div class="preview-panel__error">
        <p class="preview-panel__error-message">Unable to load product preview.</p>
        <a href="${url}" class="preview-panel__error-link">View full product page</a>
      </div>
    `;
  }

  /**
   * Handle cart add event - close panel and open cart drawer
   * @param {Event} evt - Cart add event
   */
  handleCartAdd(evt) {
    // Close this panel first
    this.close(() => {
      // Then open cart drawer
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    });
  }
}

window.customElements.define('product-preview-panel', ProductPreviewPanel);
